import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormattedNumber } from 'react-intl';
import { Stack, Type } from 'react-spark';
import { ExclamationFilledIcon } from '../../icons';
import Tooltip from '../../tooltip/Tooltip';
import { OffersV3DiscountAlert } from './OffersV3DiscountAlert';
import { formatMoney } from '../../../../utils/numbers';
import { FONT_SIZES } from '../../../../utils/novaTypography';

export class OffersV3OfferDetails extends Component {
    static propTypes = {
        selectedOffer: PropTypes.object.isRequired,
        hasQlDiscount: PropTypes.bool.isRequired,
        getsRLReturningClientDiscount: PropTypes.bool.isRequired,
        isTeamMember: PropTypes.bool.isRequired,
        hasMultipleTerms: PropTypes.bool.isRequired,
        autoPayDiscountConfig: PropTypes.object,
    };

    static defaultProps = {
        hasQlDiscount: false,
        getsRLReturningClientDiscount: false,
        isTeamMember: false,
        hasMultipleTerms: false,
    };

    render() {
        const { selectedOffer, hasQlDiscount, getsRLReturningClientDiscount, isTeamMember, hasMultipleTerms, autoPayDiscountConfig } = this.props;

        if (!selectedOffer) {
            return null;
        }

        // Map API field names to component field names
        const {
            monthlyPayment = 0,
            duration: term = 60,
            annualPercentageRate = 0,
            rate = 0,
            originationFee = 0,
            amountFinanced = 0,
        } = selectedOffer;

        // Convert percentage values (API returns as whole numbers like 13.87, we need decimals like 0.1387)
        const apr = annualPercentageRate / 100;
        const interestRate = rate / 100;
        const cashToYou = amountFinanced;

        const autoPayDiscountAmount = autoPayDiscountConfig && autoPayDiscountConfig.discountAmount || 0.25;
        const autoPayDiscountAppliesToApr = autoPayDiscountConfig && String(autoPayDiscountConfig.appliesTo).toLowerCase() === 'rate' ? false : true;
        const aprAdditionalTooltip = autoPayDiscountAmount && autoPayDiscountAppliesToApr ? ` Enrolling in autopay reduces your APR by ${autoPayDiscountAmount}%.` : '';
        const rateAdditionalTooltip = autoPayDiscountAmount && !autoPayDiscountAppliesToApr ? ` Enrolling in autopay reduces your interest rate by ${autoPayDiscountAmount}%.` : '';

        return (
            <Stack>
                <Stack.Item className="sprk-u-PaddingBottom--a">
                    <Type.DisplayFive className='sprk-u-mvn sprk-u-FontWeight--bold' style={{ fontSize: FONT_SIZES.body }}>
                        Offer details
                    </Type.DisplayFive>
                </Stack.Item>

                <Stack.Item className="sprk-u-PaddingBottom--a">
                    <Stack style={{ backgroundColor: '#F7F7F7', borderRadius: '12px', padding: '16px' }}>
                        {hasMultipleTerms ? (
                            <Fragment>
                                <Stack.Item className='sprk-u-AbsoluteCenter'>
                                    <Type.BodyFour className='sprk-u-mvn' style={{ fontSize: FONT_SIZES.body }}>
                                        Monthly payment
                                    </Type.BodyFour>
                                </Stack.Item>
                                <Stack.Item className='sprk-u-AbsoluteCenter'>
                                    <Type.DisplayFour className='sprk-u-pts sprk-u-mvn' style={{ lineHeight: 2.0, fontSize: FONT_SIZES.displaySmall }}>
                                        {formatMoney(monthlyPayment)}
                                    </Type.DisplayFour>
                                </Stack.Item>
                            </Fragment>
                        ) : (
                            <Fragment>
                                <Stack.Item className='sprk-u-AbsoluteCenter'>
                                    <Stack className="sprk-u-Flex--row sprk-u-Width-100">
                                        <Stack.Item className="sprk-u-TextAlign--center" style={{ flex: 1 }}>
                                            <Type.BodyFour className='sprk-u-mvn' style={{ fontSize: FONT_SIZES.body, lineHeight: 1.5 }}>
                                                Monthly payment
                                            </Type.BodyFour>
                                        </Stack.Item>
                                        <Stack.Item className="sprk-u-TextAlign--center" style={{ flex: 1 }}>
                                            <Type.BodyFour className='sprk-u-mvn' style={{ fontSize: FONT_SIZES.body, lineHeight: 1.5 }}>
                                                Repayment term
                                            </Type.BodyFour>
                                        </Stack.Item>
                                    </Stack>
                                </Stack.Item>

                                <Stack.Item className='sprk-u-AbsoluteCenter'>
                                    <Stack className="sprk-u-Flex--row sprk-u-Width-100">
                                        <Stack.Item className="sprk-u-TextAlign--center" style={{ flex: 1 }}>
                                            <Type.DisplayFour className='sprk-u-pts sprk-u-mvn' style={{ lineHeight: 2.0, fontSize: FONT_SIZES.displaySmall }}>
                                                {formatMoney(monthlyPayment)}
                                            </Type.DisplayFour>
                                        </Stack.Item>
                                        <Stack.Item className="sprk-u-TextAlign--center" style={{ flex: 1 }}>
                                            <Type.DisplayFour className='sprk-u-pts sprk-u-mvn' style={{ lineHeight: 2.0, fontSize: FONT_SIZES.displaySmall }}>
                                                {term} mo
                                            </Type.DisplayFour>
                                        </Stack.Item>
                                    </Stack>
                                </Stack.Item>
                            </Fragment>
                        )}
                    </Stack>
                </Stack.Item>

                <span className='sprk-c-Divider sprk-u-MarginBottom--a sprk-u-mtn' style={{ borderBottom: '1px solid #E9E9E9' }}></span>

                <Stack.Item>
                    <div className="offers-v3-details-container">
                        <div className="offers-v3-details-row">
                            <div className="offers-v3-details-row__label">
                                <Type.BodyFour className='sprk-u-mvn' style={{ fontSize: FONT_SIZES.body }}>
                                    APR
                                    <Tooltip
                                        className="black"
                                        id="offer-apr-tooltip-v3"
                                        text={`APR (Annual Percentage Rate) represents the total annual cost of your loan, including your interest rate and origination fee. These numbers are estimates.${aprAdditionalTooltip}`}>
                                        <ExclamationFilledIcon style={{
                                            height: '1em',
                                            marginLeft: '4px',
                                            marginTop: '-2px',
                                        }} />
                                    </Tooltip>
                                </Type.BodyFour>
                            </div>
                            <div className="offers-v3-details-row__value">
                                <Type.BodyFour className='sprk-u-mvn' style={{ fontSize: FONT_SIZES.body }}>
                                    <FormattedNumber
                                        value={apr}
                                        style='percent'
                                        maximumFractionDigits={3}
                                    />
                                </Type.BodyFour>
                            </div>
                        </div>

                        <div>
                            <div className="offers-v3-details-row">
                                <div className="offers-v3-details-row__label">
                                    <Type.BodyFour className='sprk-u-mvn' style={{ fontSize: FONT_SIZES.body }}>
                                        Interest rate
                                        <Tooltip
                                            className="black"
                                            id="offer-interest-rate-tooltip-v3"
                                            text={`This rate is built into your monthly payment amount and stays the same through the life of your loan.${rateAdditionalTooltip}`}>
                                            <ExclamationFilledIcon style={{
                                                height: '1em',
                                                marginLeft: '4px',
                                                marginTop: '-2px',
                                            }} />
                                        </Tooltip>
                                    </Type.BodyFour>
                                </div>
                                <div className="offers-v3-details-row__value" style={hasQlDiscount || getsRLReturningClientDiscount ? {
                                    backgroundColor: '#EFF7F3',
                                    borderRadius: '8px',
                                    padding: '8px 10px'
                                } : {}}>
                                    <Type.BodyFour className='sprk-u-mvn' style={{
                                        fontSize: FONT_SIZES.body,
                                        ...((getsRLReturningClientDiscount || hasQlDiscount) && { color: '#0B5E36' })
                                    }}>
                                        <FormattedNumber
                                            value={interestRate}
                                            style='percent'
                                            minimumFractionDigits={2}
                                            maximumFractionDigits={2}
                                        />
                                    </Type.BodyFour>
                                </div>
                            </div>

                            <OffersV3DiscountAlert isVisible={getsRLReturningClientDiscount}>
                                Loyalty pays off: As a Rocket Loans<sup style={{ fontSize: '0.6em' }}>SM</sup> client, save 0.5% on your interest rate.
                            </OffersV3DiscountAlert>

                            <OffersV3DiscountAlert isVisible={hasQlDiscount && !getsRLReturningClientDiscount}>
                                Loyalty pays off: As a Rocket Mortgage® client, save 0.5% on your interest rate.
                            </OffersV3DiscountAlert>
                        </div>

                        <div>
                            <div className="offers-v3-details-row">
                                <div className="offers-v3-details-row__label">
                                    <Type.BodyFour className='sprk-u-mvn' style={{ fontSize: FONT_SIZES.body }}>
                                        Origination fee
                                        <Tooltip
                                            className="black"
                                            id="offer-origination-fee-tooltip-v3"
                                            text="A one-time fee we'll deduct from your total loan amount.">
                                            <ExclamationFilledIcon style={{
                                                height: '1em',
                                                marginLeft: '4px',
                                                marginTop: '-2px',
                                            }} />
                                        </Tooltip>
                                    </Type.BodyFour>
                                </div>
                                <div className="offers-v3-details-row__value" style={isTeamMember ? {
                                    backgroundColor: '#EFF7F3',
                                    borderRadius: '8px',
                                    padding: '8px 10px'
                                } : {}}>
                                    <Type.BodyFour className='sprk-u-mvn' style={{
                                        fontSize: FONT_SIZES.body,
                                        ...(isTeamMember && { color: '#0B5E36' })
                                    }}>
                                        {formatMoney(originationFee, true)}
                                    </Type.BodyFour>
                                </div>
                            </div>
                            <OffersV3DiscountAlert isVisible={isTeamMember}>
                                As a FOC team member, save 50% off your origination fee.
                            </OffersV3DiscountAlert>
                        </div>

                        <div className="offers-v3-details-row">
                            <div className="offers-v3-details-row__label">
                                <Type.BodyFour className='sprk-u-mvn' style={{ fontSize: FONT_SIZES.body }}>
                                    Cash to you
                                    <Tooltip
                                        className="black"
                                        id="offer-cash-to-you-tooltip-v3"
                                        text="This is an estimate of the cash you could receive after your origination fee is deducted.">
                                        <ExclamationFilledIcon style={{
                                            height: '1em',
                                            marginLeft: '4px',
                                            marginTop: '-2px',
                                        }} />
                                    </Tooltip>
                                </Type.BodyFour>
                            </div>
                            <div className="offers-v3-details-row__value">
                                <Type.BodyFour className='sprk-u-mvn' style={{ fontSize: FONT_SIZES.body }}>
                                    {formatMoney(cashToYou, true)}
                                </Type.BodyFour>
                            </div>
                        </div>
                    </div>
                </Stack.Item>
            </Stack>
        );
    }
}
